/**
 * FilterForm component renders dynamic filter dropdowns and manages filter state.
 *
 * @param {Object} props
 * @param {Object} props.page - Page configuration.
 * @param {Object} [props.filterOptions=null] - Filter keys mapped to their options.
 * @param {Object} [props.filterDbOptions=null] - Database filter options and handlers.
 * @param {Object} [props.initialSelected={}] - Initial selected values for filters.
 * @param {Function} [props.setNoDbFilters] - Callback for non-database filter changes.
 *
 * @example
 * <FilterForm
 *   page={pageConfig}
 *   filterOptions={{ years: [2022, 2023], months: [1, 2, 3] }}
 *   initialSelected={{ years: [2023] }}
 *   setNoDbFilters={setNoDbFilters}
 * />
 */
import { useState, useEffect } from 'react';

import { AvForm } from 'availity-reactstrap-validation';
import { FormGroup } from 'reactstrap';

import FilterDropdown from '../../components/Filter/FilterDropdown';

export const FilterForm = ({
  page = {},
  filterOptions = null,
  filterDbOptions = null,
  setNoDbFilters
}) => {
  const { showDbFilter = false } = page;
  const { filterFormValues, singleSelect, allowToggleSingle, initialSelected, setFilterFormValues, filterCustomContent, filterFormSubmit, filterFormReset } = filterDbOptions || {};
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
  // // Hook to create a dynamic filter state
  // const useFilter = (defaultValue = []) => {
  //   const [selectedOptions, setSelectedOptions] = useState(defaultValue);
  //   const handleFilterChange = (option) => {
  //     setSelectedOptions((prev) =>
  //       prev.includes(option) ? prev.filter((s) => s !== option) : [...prev, option]
  //     );
  //   };
  //   const resetFilter = () => {
  //     setSelectedOptions(defaultValue);
  //   };
  //   return { selectedOptions, handleFilterChange, resetFilter };
  // };
  // Dynamically build hooks for all filters
  // const filterKeys = Object.keys(filterOptions || {});
  // const filters = useDynamicFilters(filterOptions);
  // const filters = useMemo(() => {
  //   const obj = {};
  //   filterKeys.forEach((key) => {
  //     obj[key] = useFilter([]);
  //   });
  //   return obj;
  // }, [filterKeys.join(',')]); // rerun only if keys change

  // const useFilter = (defaultValue = []) => {
  //   const [selectedOptions, setSelectedOptions] = useState(defaultValue);
  //   const handleFilterChange = (option) => {
  //     setSelectedOptions((prev) => (prev.includes(option) ? prev.filter((s) => s !== option) : [...prev, option]));
  //   };
  //   const resetFilter = () => {
  //     setSelectedOptions(defaultValue);
  //   };
  //   return { selectedOptions, handleFilterChange, resetFilter };
  // };

  // const { selectedOptions: selectedYearOptions, handleFilterChange: handleYearOptionFilterChange, resetFilter: resetYearFilter } = useFilter([String(currentYear)]);
  // const { selectedOptions: selectedMonthOptions, handleFilterChange: handleMonthOptionFilterChange, resetFilter: resetMonthFilter } = useFilter([currentMonthKey]);
  // const { selectedOptions: selectedPaymentMethods, handleFilterChange: handlePaymentMethodFilterChange, resetFilter: resetPaymentMethodFilter } = useFilter([]);
  // const { selectedOptions: selectedOrderStatuses, handleFilterChange: handleOrderStatusFilterChange, resetFilter: resetOrderStatusFilter } = useFilter([]);
  // const { selectedOptions: selectedDateTypes, handleFilterChange: handleDateTypeFilterChange, resetFilter: resetDateTypeFilter } = useFilter([]);
  // const { selectedOptions: selectedPaymentStatuses, handleFilterChange: handlePaymentStatusFilterChange, resetFilter: resetPaymentStatusFilter } = useFilter([]);

  // useEffect(() => {
  //   // Set default values on load
  //   if (typeof setFilterFormValues === 'function') {
  //     const defaultValues = {
  //       years: selectedYearOptions.map(Number),
  //       months: selectedMonthOptions.map(Number),
  //       paymentMethods: selectedPaymentMethods,
  //       orderStatuses: selectedOrderStatuses,
  //       dateTypes: selectedDateTypes,
  //       paymentStatuses: selectedPaymentStatuses
  //     };

  //     setFilterFormValues(defaultValues);
  //   }
  // }, []);

  // useEffect(() => {
  //   if (typeof setFilterFormValues === 'function') {
  //     setFilterFormValues((prev) => ({
  //       ...prev,
  //       years: selectedYearOptions.map(Number),
  //       months: selectedMonthOptions.map(Number),
  //       paymentMethods: selectedPaymentMethods,
  //       orderStatuses: selectedOrderStatuses,
  //       dateTypes: selectedDateTypes,
  //       paymentStatuses: selectedPaymentStatuses,
  //       clientName: '',
  //       startDate: '', 
  //       endDate: '',
  //     }));
  //   }
  // }, [selectedYearOptions, selectedMonthOptions, selectedPaymentMethods, selectedOrderStatuses, selectedDateTypes, selectedPaymentStatuses]);  

  /**
   * Custom React hook for managing dynamic filter states in a form.
   *
   * This hook initializes filter states based on provided filter options and initial values,
   * and provides handlers to update, reset individual, or reset all filters. It also supports
   * synchronizing filter state with external state setters, and handles conversion of specific
   * filter keys (e.g., 'years', 'months') to numeric values when updating external form values.
   *
   * @param {Object} filterOptions - An object mapping filter keys to their available options.
   * @param {Object} [initialSelected={}] - An object specifying the initially selected filter values.
   * @param {Object} [filterFormValues={}] - An object representing the current filter form values.
   * @param {Function|null} [setNoDbFilters=null] - Optional setter function to update non-database filters.
   * @param {Function|null} [setFilterFormValues=null] - Optional setter function to update filter form values.
   * @returns {Object} An object containing:
   *   - filters: The current filter state.
   *   - handleFilterChange: Function to toggle a filter option.
   *   - resetFilter: Function to reset a specific filter to its initial state.
   *   - resetAllFilters: Function to reset all filters to their initial state.
   */
  const useDynamicFilters = (filterOptions, initialSelected = {}, filterFormValues = {}, setNoDbFilters = null, setFilterFormValues = null) => {
    const initialState = Object.fromEntries(
      Object.entries(filterOptions || {}).map(([key, options]) => [
        key,
        (filterFormValues?.[key] || []).map(String),
      ])
    );
    const [filters, setFilters] = useState(initialState);
    const handleFilterChange = (key, option) => {
      setFilters((prev) => {
        const prevOptions = prev[key] || [];

        if (singleSelect) {
          const isSame = prevOptions.length === 1 && prevOptions[0] === option;
          return {
            ...prev,
            [key]: allowToggleSingle && isSame ? [] : [option], // only one
          };
        }

        return {
          ...prev,
          [key]: prevOptions.includes(option)
            ? prevOptions.filter((opt) => opt !== option)
            : [...prevOptions, option],
        };
      });
    };
    const resetFilter = (key) => {
      setFilters((prev) => ({
        ...prev,
        [key]: initialState[key] || [],
      }));
    };
    const resetAllFilters = () => {
      setFilters(initialSelected);
    };
    useEffect(() => {
      const noDbFilterKeys = ['statuses', 'years', 'months']; // add more keys as needed
      // Split filters into two objects
      const noDbFilters = {};
      const formFilters = {};
      for (const [key, value] of Object.entries(filters)) {
        if (!noDbFilterKeys.includes(key)) {
          noDbFilters[key] = value;
        } else {
          formFilters[key] = value;
        }
      }

      if (setNoDbFilters) { setNoDbFilters(noDbFilters); }

      if (setFilterFormValues) {
        // convert specific keys back to numbers
        const numericKeys = ['years', 'months'];
        const convertedFilters = Object.fromEntries(
          Object.entries(formFilters).map(([key, value]) => {
            if (numericKeys.includes(key)) {
              return [key, value.map(Number)];
            }
            return [key, value];
          })
        );
        setFilterFormValues(prev => ({
          ...prev,
          ...convertedFilters
        }));
      }
    }, [filters]);
    return {
      filters,
      handleFilterChange,
      resetFilter,
      resetAllFilters,
    };
  };
  /**
   * Handles the submission of filter data from the form.
   * Invokes the filterFormSubmit function with the current filters and form values.
   *
   * @async
   * @param {Event} e - The event object from the form submission.
   * @param {Object} values - The values entered in the filter form.
   * @returns {Promise<void>} A promise that resolves when the filter submission is complete.
   */
  const handleFilterDataSubmit = async (e, values) => filterFormSubmit(filters, values)
  /**
   * Resets the filter form and clears all applied filters.
   * Calls both `filterFormReset` to reset the form fields and `resetAllFilters` to remove all active filters.
   */
  const resetFilterData = () => {
    filterFormReset()
    resetAllFilters()
  }
  /**
   * Custom hook that manages dynamic filter state and handlers for a filter form.
   *
   * @param {Array} filterOptions - The available filter options for the form.
   * @param {Object} initialSelected - The initial selected filter values.
   * @param {Object} filterFormValues - The current values of the filter form.
   * @param {Function} setNoDbFilters - Setter function for filters not stored in the database.
   * @param {Function} setFilterFormValues - Setter function for the filter form values.
   * @returns {Object} An object containing:
   *   - filters: The current filter state.
   *   - handleFilterChange: Handler to update filter values.
   *   - resetFilter: Function to reset a specific filter.
   *   - resetAllFilters: Function to reset all filters to their initial state.
   */
  const { filters, handleFilterChange, resetFilter, resetAllFilters } = useDynamicFilters(filterOptions, initialSelected, filterFormValues, setNoDbFilters, setFilterFormValues);

  return (
    <AvForm className='d-flex ms-2' id='filter-date-form' onValidSubmit={handleFilterDataSubmit}>
      {Object.entries(filterOptions || {}).map(([key, options]) => (
        <div key={key} className='me-1'>
          <FilterDropdown
            singleSelect={singleSelect}
            allowToggleSingle={allowToggleSingle}
            dropdownOpen={dropdownOpen}
            toggleDropdown={toggleDropdown}
            selectedOptions={filters?.[key] || []}
            handleFilterChange={(option) => handleFilterChange(key, option)}
            resetFilters={() => resetFilter(key)}
            options={options}
            filterName={key
              .split(/(?=[A-Z])/)
              .join(" ")
              .replace(/^./, (char) => char.toUpperCase())}
          />
        </div>
      ))}
      {filterCustomContent}
      {showDbFilter && (
        <>
          {/* <FormGroup className='pe-1'>
            <button className='btn btn-success' id='dblclick'>
              Filter
            </button>
          </FormGroup> */}
          <FormGroup className=''>
            <button type='reset' onClick={resetFilterData} className='btn btn-primary'>
              Reset
            </button>
          </FormGroup>
        </>
      )}
    </AvForm>
  );
}